import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import TitleBar from "../../designSystem/c-organism/titleBar/TitleBar";
import Icon from "../../designSystem/a-atoms/icons/Icon";
import { getFeedback, getSummary } from "../../services/insightsApi";
import style from "./Insights.module.css";

const SENTIMENT_ICONS = {
  positive: "sentiment_satisfied",
  negative: "sentiment_dissatisfied",
  neutral: "sentiment_neutral",
};

const InsightsExplore = () => {
  const org = useSelector((state) => state.orgStatus.value);

  const [records, setRecords] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filterCategory, setFilterCategory] = useState("");
  const [filterSubCategory, setFilterSubCategory] = useState("");
  const [filterSentiment, setFilterSentiment] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (!org?.payload) return;
    loadData();
  }, [org?.payload]);

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      const [feedbackRes, summaryRes] = await Promise.all([
        getFeedback(org.payload, { limit: 500 }),
        getSummary(org.payload),
      ]);
      setRecords(feedbackRes.records);
      setCategories(
        Object.entries(summaryRes.categories || {}).map(([id, c]) => ({
          id,
          ...c,
        }))
      );
      setSources(
        Object.entries(summaryRes.sources || {}).map(([id, s]) => ({
          id,
          ...s,
        }))
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const selectedCategoryObj = filterCategory
    ? categories.find((c) => c.name === filterCategory)
    : null;

  const subCategories = selectedCategoryObj?.subCategories
    ? Object.entries(selectedCategoryObj.subCategories).map(([id, sc]) => ({
        id,
        ...sc,
      }))
    : [];

  const filtered = records.filter((r) => {
    if (filterCategory && r.category !== filterCategory) return false;
    if (filterSubCategory && r.subCategory !== filterSubCategory) return false;
    if (filterSentiment && r.sentiment !== filterSentiment) return false;
    if (filterSource && r.source !== filterSource) return false;
    if (searchText && !r.text?.toLowerCase().includes(searchText.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="mainContentInner">
      <TitleBar
        title="Explore Feedback"
        breadcrumb={[
          { name: "Home", url: "/" },
          { name: "Insights", url: "/insights" },
        ]}
      />

      <div className="contentPadding">
        {error && <div className={style.resultError}><Icon>error</Icon> {error}</div>}

        <div className={style.exploreFilters}>
          <div className={style.searchField}>
            <Icon>search</Icon>
            <input
              type="text"
              placeholder="Search feedback..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          <select
            className={style.filterSelect}
            value={filterCategory}
            onChange={(e) => {
              setFilterCategory(e.target.value);
              setFilterSubCategory("");
            }}
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.name}>
                {c.name} ({c.count})
              </option>
            ))}
          </select>

          {subCategories.length > 0 && (
            <select
              className={style.filterSelect}
              value={filterSubCategory}
              onChange={(e) => setFilterSubCategory(e.target.value)}
            >
              <option value="">All Sub-categories</option>
              {subCategories.map((sc) => (
                <option key={sc.id} value={sc.name}>
                  {sc.name} ({sc.count})
                </option>
              ))}
            </select>
          )}

          <select
            className={style.filterSelect}
            value={filterSentiment}
            onChange={(e) => setFilterSentiment(e.target.value)}
          >
            <option value="">All Sentiment</option>
            <option value="positive">Positive</option>
            <option value="negative">Negative</option>
            <option value="neutral">Neutral</option>
          </select>

          <select
            className={style.filterSelect}
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
          >
            <option value="">All Sources</option>
            {sources.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div className={style.resultCount}>
          {loading ? "Loading..." : `${filtered.length} record${filtered.length !== 1 ? "s" : ""}`}
        </div>

        {!loading && (
          <div className={style.feedbackList}>
            {filtered.map((record) => (
              <div key={record.id} className={style.feedbackCard}>
                <div className={style.feedbackHeader}>
                  <div className={`${style.sentimentBadge} ${style[record.sentiment]}`}>
                    <Icon>{SENTIMENT_ICONS[record.sentiment] || "help"}</Icon>
                  </div>
                  <span className={style.feedbackCategory}>{record.category}</span>
                  {record.subCategory && record.subCategory !== record.category && (
                    <span className={style.feedbackSubCategory}>{record.subCategory}</span>
                  )}
                  {record.rating && (
                    <span className={style.feedbackRating}>
                      <Icon>star</Icon> {record.rating}
                    </span>
                  )}
                  <span className={style.feedbackDate}>{record.date}</span>
                </div>
                <p className={style.feedbackText}>
                  {record.text || <em>No text content</em>}
                </p>
              </div>
            ))}

            {filtered.length === 0 && !loading && (
              <div className={style.noResults}>No feedback matches your filters.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InsightsExplore;
